import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface StatusOption {
  id: string;
  name: string;
}

interface TaskStatusSelectProps {
  taskId: string;
  currentStatusId: string;
  currentStatusName: string;
  statuses: StatusOption[];
  canEdit: boolean;
  hideLabel?: boolean;
}

export function TaskStatusSelect({ taskId, currentStatusId, currentStatusName, statuses, canEdit, hideLabel }: TaskStatusSelectProps) {
  const { toast } = useToast();

  const updateMutation = useMutation({
    mutationFn: async (statusId: string) =>
      apiRequest("PATCH", `/api/tasks/${taskId}`, { statusId }),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) =>
      toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  if (!canEdit) {
    return (
      <div className="flex items-center gap-1.5">
        {!hideLabel && <span className="text-xs text-muted-foreground">Status:</span>}
        <Badge variant="secondary" data-testid="task-badge-status">{currentStatusName}</Badge>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {!hideLabel && <Label className="text-xs text-muted-foreground whitespace-nowrap">Status:</Label>}
      <Select
        value={currentStatusId}
        onValueChange={v => updateMutation.mutate(v)}
        disabled={updateMutation.isPending}
      >
        <SelectTrigger className="h-7 w-auto min-w-[100px] text-sm gap-1" data-testid="task-select-status-inline">
          <SelectValue>{currentStatusName}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {statuses.map(s => (
            <SelectItem key={s.id} value={s.id} data-testid={`option-status-${s.id}`}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
